import { Link, useNavigate } from "react-router-dom";
import Logo from "../../../assets/logoOlin.png";
import { BsCartPlus, BsChatText } from "react-icons/bs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addToCart } from "../../../services/purchaseApi";
import { useNotification } from "../../../components/context/NotificationContext";
import { getLabelConfig } from "../../../config/labelConfig";
import { checkDoctorAvailability } from "../../../services/telemedicineApi";
import { useContext, useState } from "react";
import { GlobalStateContext } from "../../../components/context/GlobalStateContext";
import {
  getDetailUserProfile,
  getListMyLocation,
} from "../../../services/profileApi";
import LoadingBackdrop from "../../common/LoadingBackdrop";

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const ProductCardNew = ({ item, isLoading, mutate }) => {
  const companyId = localStorage.getItem("companyId");
  const machineId = localStorage.getItem("MachineId");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const label = getLabelConfig();
  const { isLogin, topicId } = useContext(GlobalStateContext);

  const [productDetail, setProductDetail] = useState(null);

  const handleAddToCart = (body) => {
    mutateAddToCart(body);
  };

  const handleSuccessAddToCart = () => {
    mutate();
    queryClient.invalidateQueries({
      queryKey: ["product-list"],
    });
    queryClient.invalidateQueries({
      queryKey: ["count-cart"],
    });
    queryClient.invalidateQueries({
      queryKey: ["count-cart-jwt"],
    });
    queryClient.invalidateQueries({
      queryKey: ["cart-detail"],
    });
  };

  const { data: dataList } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
    enabled: !!isLogin,
  });

  const { data: dataProfile } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const { mutate: mutateAddToCart, isLoading: isAddToCartLoading } =
    useMutation({
      mutationFn: addToCart,
      onSuccess: () => {
        showNotification("Berhasil menambahkan produk ke keranjang", "success");
        handleSuccessAddToCart();
      },
    });

  const { mutate: mutateCheckDoctor, isPending: isLoadingCheckDoctor } =
    useMutation({
      mutationFn: checkDoctorAvailability,
      onSuccess: (data) => {
        if (!isLogin) {
          navigate("/profile");
        } else if (isLogin && dataList === null) {
          showNotification("Silahkan tambahkan alamat terlebih dahulu", "info");
          navigate("/location-address/my-address/new");
        } else if (dataProfile.nik === "0") {
          showNotification(
            "Silahkan tambahkan No. NIK terlebih dahulu",
            "info",
          );
          navigate("/profile/change");
        } else if (topicId && topicId.trim() !== "") {
          navigate(`/consultation/chat-with-doctor/${topicId}`, {
            state: { type: 2 },
          });
        } else {
          navigate("/consultation/detail-form", {
            state: {
              productDetail,
              doctorData: data,
            },
          });
        }
      },
      onError: (err) => {
        if (err?.message === "Token expired") {
          showNotification("Silahkan login terlebih dahulu", "info");
          navigate("/profile");
        }
        showNotification(err?.message, "error");
      },
    });

  return (
    <>
      <LoadingBackdrop isLoading={isLoadingCheckDoctor} />
      {isLoading ? (
        <div className="group p-1 lg:p-2 rounded-xl bg-white border border-gray-200 shadow-none lg:shadow-sm hover:shadow-md transition-all duration-300 animate-pulse">
          <div className="bg-slate-100 w-full aspect-square rounded-lg mb-1 lg:mb-2"></div>
          <div className="p-1">
            <div className="bg-slate-200 h-5 w-3/5 rounded-md"></div>
            <div className="flex items-end justify-between mt-1">
              <div className="h-8 bg-slate-200 rounded-md w-1/2"></div>
              <div className="h-10 w-10 bg-slate-200 rounded-md"></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="group p-1 lg:p-2 rounded-xl lg:rounded-xl bg-white border border-gray-200 shadow-none lg:shadow-sm transition-all duration-300 hover:border-secondary flex flex-col h-full hover:bg-secondaryActive">
          <div
            onClick={() => navigate(`/purchase/product-detail/${item.id}`)}
            className="flex-1"
          >
            <div className="bg-[#F5F5F5] aspect-square w-full rounded-md lg:rounded-lg flex items-center justify-center mb-1 lg:mb-4">
              <div className="relative w-full h-full rounded-lg overflow-hidden bg-white">
                <img
                  src={
                    item?.media[0] !== ""
                      ? item?.media[0]?.includes("{")
                        ? item?.media[0]?.substring(
                            1,
                            item?.media[0]?.length - 1,
                          )
                        : item?.media[0]
                      : Logo
                  }
                  alt={item?.product_name || "Product Image"}
                  width={500}
                  height={500}
                  className="object-cover object-center w-full h-full transition duration-300 group-hover:scale-105"
                  priority={false}
                />
                {item.label_id === 3 ||
                item.label_id === 4 ||
                item.label_id === 5 ? (
                  <div className="absolute top-2 right-2 bg-red-600 h-4 w-4 rounded-full"></div>
                ) : (
                  <div className="absolute top-2 right-2 bg-green-600 h-4 w-4 rounded-full"></div>
                )}
                {item.stock === 0 && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-200 opacity-50 text-error h-24 w-24 rounded-full">
                    <div className="w-full h-full flex items-center justify-center font-bold">
                      HABIS
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-auto px-1 pb-1">
            <div className="lg:mb-2 flex-1">
              <div
                onClick={() => navigate(`/purchase/product-detail/${item.id}`)}
                className="flex-1"
              >
                <p className="text-md lg:text-base font-medium line-clamp-2 leading-tight min-h-8 lg:min-h-[36px] overflow-hidden cursor-pointer hover:text-primary transition-colors text-slate-700">
                  {item?.name}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-end">
              <p className="text-error font-semibold text-lg lg:text-lg tabular-nums">
                Rp{formatRupiah(item.price)}
              </p>

              <div
                onClick={() => {
                  if (!(item.qty_cart === item.stock)) {
                    if (
                      item.label_id === 3 ||
                      item.label_id === 4 ||
                      item.label_id === 5
                    ) {
                      setProductDetail(item);
                      mutateCheckDoctor({
                        internal_only:
                          label.brandName === "Orlin" ? true : false,
                      });
                    } else {
                      handleAddToCart({
                        company_id: Number(companyId),
                        price: item.price,
                        product_id: item.id,
                        product_name: item.name,
                        image: item.media,
                        product_type: item.uom,
                        stock: item.stock,
                        quantity: item.qty_cart + 1,
                        prescription_image: [],
                        check_state: true,
                        uuid: machineId,
                      });
                    }
                  }
                }}
                className={`p-1.5 rounded-md border border-slate-300 transition-all duration-300 ${item.qty_cart === item.stock ? "bg-slate-300 text-white cursor-default" : "bg-white text-slate-500 cursor-pointer hover:border-secondary hover:text-secondary"}`}
              >
                {item.label_id === 3 ||
                item.label_id === 4 ||
                item.label_id === 5 ? (
                  <BsChatText size={18} />
                ) : (
                  <BsCartPlus size={18} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCardNew;
